import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { ScrollArea } from 'components/ui/scroll-area';
import { 
  ArrowLeft, MessageCircle, Users, Search, Circle, Plus, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import RealtimeChat from 'components/RealtimeChat';

export default function Messages() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const chatWith = searchParams.get('chat_with');
  const [activeChat, setActiveChat] = useState(chatWith ? { type: 'direct', id: chatWith } : null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  useEffect(() => {
    fetchConversations();
    fetchCircles();
    fetchOnlineUsers();
    
    // Poll online users every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchCircles = async () => {
    try {
      const response = await api.get('/care-circles');
      setCircles(response.data.circles || []);
    } catch (error) {
      console.error('Failed to fetch circles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/ws/online-users');
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  };

  const isUserOnline = (userId) => onlineUsers.some(u => u.user_id === userId);

  const filteredConversations = conversations.filter(conv =>
    conv.other_user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCircles = circles.filter(circle =>
    circle.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // If there's an active chat, show the chat component
  if (activeChat) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="messages-page">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <RealtimeChat
              circleId={activeChat.type === 'circle' ? activeChat.id : null}
              recipientId={activeChat.type === 'direct' ? activeChat.id : null}
              onBack={() => setActiveChat(null)}
            />
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Messages</h1>
              <p className="text-xs sm:text-sm text-slate-500">Real-time chat with your care team</p>
            </div>
          </div>
          <Badge className="bg-teal-100 text-teal-700">
            <Circle className="w-2 h-2 mr-1 fill-current" />
            {onlineUsers.length} Online
          </Badge>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-conversations"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : (
          <>
            {/* Care Circles */}
            {filteredCircles.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-teal-500" />
                    Care Circles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {filteredCircles.map((circle) => (
                    <button
                      key={circle.circle_id}
                      onClick={() => setActiveChat({ type: 'circle', id: circle.circle_id })}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                      data-testid={`circle-${circle.circle_id}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-6 h-6 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{circle.name}</h4>
                        <p className="text-sm text-slate-500">
                          {circle.members?.length || 0} members
                        </p>
                      </div>
                      <Badge variant="secondary" className="flex-shrink-0">
                        Group
                      </Badge>
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Direct Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageCircle className="w-5 h-5 text-teal-500" />
                  Direct Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No conversations yet</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Start chatting with members of your care circle
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredConversations.map((conv) => (
                      <button
                        key={conv.other_user?.user_id}
                        onClick={() => setActiveChat({ type: 'direct', id: conv.other_user?.user_id })}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                        data-testid={`conversation-${conv.other_user?.user_id}`}
                      >
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                            {conv.other_user?.picture ? (
                              <img
                                src={conv.other_user.picture}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-lg font-bold text-teal-700">
                                {conv.other_user?.name?.charAt(0) || '?'}
                              </span>
                            )}
                          </div>
                          {isUserOnline(conv.other_user?.user_id) && (
                            <Circle className="absolute bottom-0 right-0 w-3 h-3 text-green-500 fill-green-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-900 truncate">
                              {conv.other_user?.name}
                            </h4>
                            {conv.last_message?.created_at && (
                              <span className="text-xs text-slate-400">
                                {new Date(conv.last_message.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 truncate">
                            {conv.last_message?.content || 'No messages yet'}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-teal-500 text-white flex-shrink-0">
                            {conv.unread_count}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Online Now */}
            {onlineUsers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Circle className="w-4 h-4 text-green-500 fill-green-500" />
                    Online Now
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {onlineUsers.map((onlineUser) => (
                      <button
                        key={onlineUser.user_id}
                        onClick={() => setActiveChat({ type: 'direct', id: onlineUser.user_id })}
                        className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                        data-testid={`online-user-${onlineUser.user_id}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                          {onlineUser.picture ? (
                            <img src={onlineUser.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs font-bold text-teal-700">
                              {onlineUser.name?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{onlineUser.name}</span>
                        <Circle className="w-2 h-2 text-green-500 fill-green-500" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
